import React, { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  'In Progress': {
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/20',
    border: 'border-cyan-500/40',
    icon: Loader2,
    iconClass: 'animate-spin',
  },
  'Waiting': {
    color: 'text-gray-400',
    bg: 'bg-slate-700/40',
    border: 'border-slate-600',
    icon: Clock,
    iconClass: '',
  },
  'Completed': {
    color: 'text-green-400',
    bg: 'bg-green-500/20',
    border: 'border-green-500/40',
    icon: CheckCircle2,
    iconClass: '',
  },
};

const Queue = () => {
  const [visible, setVisible] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/queue`);
      setQueueItems(res.data);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, [fetchQueue]);

  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <CalendarCheck className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-300 via-teal-400 to-cyan-400 bg-clip-text text-transparent mb-4">
            Commission Queue
          </h1>
          <p className="text-xl text-gray-300">
            Current commission queue and status
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
            <p className="text-gray-400 mt-3">Loading queue...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && queueItems.length === 0 && (
          <div
            className={`text-center text-gray-400 py-20 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-xl">Queue is empty!</p>
            <p className="text-sm mt-2">No commissions in the queue right now.</p>
          </div>
        )}

        {/* Queue list */}
        {!loading && queueItems.length > 0 && (
          <div className="space-y-4">
            {queueItems.map((item, index) => {
              const status = statusConfig[item.status] || statusConfig['Waiting'];
              const StatusIcon = status.icon;
              return (
                <div
                  key={item.id}
                  className={`transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  <div
                    className={`rounded-xl border backdrop-blur-xl shadow-lg transition-all duration-300 hover:scale-[1.02] ${
                      item.status === 'In Progress'
                        ? 'bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border-cyan-500/40 shadow-cyan-500/10'
                        : item.status === 'Completed'
                        ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/40 shadow-green-500/10'
                        : 'bg-slate-800/40 border-slate-700 hover:border-teal-500/30'
                    }`}
                  >
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                            item.status === 'In Progress'
                              ? 'bg-cyan-500/20 text-cyan-400'
                              : item.status === 'Completed'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700/60 text-gray-400'
                          }`}
                        >
                          {item.position}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{item.client}</h3>
                          <p className="text-gray-400 text-sm">{item.commission_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusIcon
                          className={`w-4 h-4 ${status.color} ${status.iconClass}`}
                        />
                        <span
                          className={`text-sm font-medium ${status.color} px-3 py-1 rounded-full ${status.bg} border ${status.border}`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div
          className={`mt-8 text-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <div className="rounded-xl border bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border-cyan-400/30 backdrop-blur-xl p-4">
            <p className="text-cyan-300 text-sm">
              Queue updates in real-time. Typical turnaround is 3-7 business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Queue;
